import axios, {
	type AxiosInstance,
	type InternalAxiosRequestConfig,
	type AxiosResponse,
	type AxiosRequestConfig,
	type AxiosError
} from 'axios';
import { jwtDecode } from 'jwt-decode';
import { captureException, addBreadcrumb } from './sentry/sentry-utils';
import { ERROR_LEVELS, TAGS } from '../constants/sentry-constants';
import { toast } from '@/components/ui/sonner';
import { clearAllPermissionsStorage } from '@/stores/permissions.store';
import { SELECTED_BRAND_KEY } from '@/stores/sentinel-workspace.store';
import { clearAccountScopedStorage } from '@/lib/account-storage';
import { API_BASE_URL, MACHINE_BASE_URL } from '@/config/api';

interface JwtPayload {
	iss?: string;
	sub?: string;
	aud?: string[] | string;
	exp?: number;
	nbf?: number;
	iat?: number;
	jti?: string;
}

interface CustomAxiosInternalRequestConfig extends InternalAxiosRequestConfig {
	authRequired?: boolean;
	_retry?: boolean;
	__sentryStartTime?: number;
}

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
	authRequired?: boolean;
}

interface ErrorResponseData {
	message?: string;
	details?: string;
	detail?: string;
	reason?: string;
	[key: string]: unknown;
}

// Get API configuration from environment variables
const API_TIMEOUT = Number(import.meta.env.VITE_API_TIMEOUT) || 60000;
const ENABLE_LOGGING = import.meta.env.VITE_ENABLE_LOGGING === 'true';

export const getMachineOrigin = (): string => new URL(MACHINE_BASE_URL, window.location.origin).origin;

// Define the base configuration for the Axios instance
export const api: AxiosInstance = axios.create({
	baseURL: API_BASE_URL,
	timeout: API_TIMEOUT,
	headers: {
		'Content-Type': 'application/json'
	},
	// Repeat array params (`?status=open&status=shipped`) instead of axios's default
	// bracket form (`?status[]=open`). Express 5's default query parser does not unwrap
	// brackets — it would read the literal key `status[]` and the filter would be dropped
	// silently, returning unfiltered results.
	paramsSerializer: { indexes: null }
});

// Logging utility
const log = (...args: unknown[]) => {
	if (ENABLE_LOGGING) {
		console.log('[API]', ...args);
	}
};

// Storage keys (must match use-auth.ts)
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const CURRENT_USER_KEY = 'currentUser';
const ADMIN_SNAPSHOT_KEY = 'adminSnapshot';
const REFRESH_ENDPOINT = '/auth/refresh';

// Token management utilities
const getAccessToken = (): string | null => localStorage.getItem(ACCESS_TOKEN_KEY);
const getRefreshToken = (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY);

export const isTokenExpired = (token: string): boolean => {
	try {
		const { exp } = jwtDecode<JwtPayload>(token);
		if (!exp) return true;
		return Date.now() / 1000 >= exp;
	} catch {
		return true;
	}
};

export const AUTH_CLEARED_EVENT = 'auth:cleared';

export const clearAuthAndRedirect = (): void => {
	localStorage.removeItem(ACCESS_TOKEN_KEY);
	localStorage.removeItem(REFRESH_TOKEN_KEY);
	localStorage.removeItem(CURRENT_USER_KEY);
	localStorage.removeItem(ADMIN_SNAPSHOT_KEY);
	localStorage.removeItem(SELECTED_BRAND_KEY);
	clearAllPermissionsStorage();
	clearAccountScopedStorage();
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent(AUTH_CLEARED_EVENT));
	}
	const currentPath = window.location.pathname;
	const hasPendingSso = new URLSearchParams(window.location.search).has('sso_token');

	if (!hasPendingSso && currentPath !== '/login' && currentPath !== '/super_admin/login') {
		window.location.href = '/login';
	}
};

const storeTokens = (accessToken: string, refreshToken: string, user?: Record<string, unknown>): void => {
	localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
	localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
	if (user) {
		localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(user));
	}
};

/**
 * Serializes concurrent refresh attempts so only one network call is made.
 * All callers await the same promise until it resolves or rejects.
 */
let refreshPromise: Promise<string> | null = null;

interface RefreshApiResponse {
	statusCode: number;
	data: {
		token: string;
		refreshToken: string;
		user: Record<string, unknown>;
	};
	message: string;
	success: boolean;
}

const refreshAccessToken = async (): Promise<string> => {
	if (refreshPromise) return refreshPromise;

	refreshPromise = (async () => {
		const storedRefreshToken = getRefreshToken();
		if (!storedRefreshToken || isTokenExpired(storedRefreshToken)) {
			throw new Error('Refresh token missing or expired');
		}

		log('Refreshing access token…');
		const response = await axios.post<RefreshApiResponse>(
			`${API_BASE_URL}${REFRESH_ENDPOINT}`,
			{ refreshToken: storedRefreshToken },
			{ headers: { 'Content-Type': 'application/json' } }
		);

		const { token, refreshToken: newRefreshToken, user } = response.data.data;
		storeTokens(token, newRefreshToken, user);
		log('Token refreshed successfully');
		return token;
	})().finally(() => {
		refreshPromise = null;
	});

	return refreshPromise;
};

// Request interceptor: attach token, refresh proactively if expired
api.interceptors.request.use(
	async (config: CustomAxiosInternalRequestConfig): Promise<CustomAxiosInternalRequestConfig> => {
		const startTime = Date.now();
		log('Request:', config.method?.toUpperCase(), config.url);
		config.__sentryStartTime = startTime;

		if (config.authRequired === false) return config;

		let accessToken = getAccessToken();

		if (accessToken && isTokenExpired(accessToken)) {
			try {
				accessToken = await refreshAccessToken();
			} catch {
				log('Proactive token refresh failed, clearing auth');
				clearAuthAndRedirect();
				return Promise.reject(new Error('Token expired'));
			}
		}

		if (accessToken) {
			(config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
			log('Authorization header set');
		}

		return config;
	},
	(error: AxiosError) => {
		log('Request error:', error);

		captureException(error, {
			tags: {
				[TAGS.ERROR_TYPE]: 'axios_request'
			},
			contexts: {
				http: {
					method: error.config?.method?.toUpperCase(),
					url: error.config?.url
				}
			}
		});

		return Promise.reject(error);
	}
);

// Response interceptor for handling responses globally
api.interceptors.response.use(
	(response: AxiosResponse) => {
		const configWithSentry = response.config as CustomAxiosInternalRequestConfig;
		const rawDuration = configWithSentry.__sentryStartTime ? Date.now() - configWithSentry.__sentryStartTime : undefined;
		// Validate duration is non-negative (handles clock adjustments)
		const duration = rawDuration !== undefined ? Math.max(0, rawDuration) : undefined;

		log('Response:', response.status, response.config.url);

		// Only add breadcrumb for slow responses (>1000ms) or errors (status >= 400)
		const method = response.config.method?.toUpperCase() || 'UNKNOWN';
		const url = response.config.url || '';
		const isSlow = duration !== undefined && duration > 1000;
		const isError = response.status >= 400;

		if (isSlow || isError) {
			addBreadcrumb({
				category: 'http',
				type: 'http',
				level: isError ? ERROR_LEVELS.ERROR : ERROR_LEVELS.WARNING,
				message: `${method} ${url} - ${response.status}${isSlow ? ' (slow)' : ''}`,
				data: {
					method,
					url,
					statusCode: response.status,
					duration: duration !== undefined ? duration : undefined,
					unit: duration !== undefined ? 'millisecond' : undefined,
					isSlow
				}
			});
		}

		return response;
	},
	async (error: AxiosError<ErrorResponseData>) => {
		// Extract error message from various response formats
		const errorData = error.response?.data;
		if (errorData) {
			if (typeof errorData.message === 'string') {
				error.message = errorData.message;
			} else if (typeof errorData.details === 'string') {
				error.message = errorData.details;
			} else if (typeof errorData.detail === 'string') {
				error.message = errorData.detail;
			} else if (typeof errorData.reason === 'string') {
				error.message = errorData.reason;
			}
		}

		const originalRequest = error.config as CustomAxiosInternalRequestConfig | undefined;
		const rawDuration = originalRequest?.__sentryStartTime ? Date.now() - originalRequest.__sentryStartTime : undefined;
		// Validate duration is non-negative (handles clock adjustments)
		const duration = rawDuration !== undefined ? Math.max(0, rawDuration) : undefined;

		// Add Sentry breadcrumb for API error (always add for errors)
		addBreadcrumb({
			category: 'http',
			type: 'http',
			level: ERROR_LEVELS.ERROR,
			message: `${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} - ${error.response?.status || 'Network Error'}`,
			data: {
				method: originalRequest?.method?.toUpperCase(),
				url: originalRequest?.url,
				statusCode: error.response?.status,
				error: error.message,
				duration
			}
		});

		// On 401: attempt token refresh + retry (skip for refresh endpoint itself)
		if (
			originalRequest &&
			originalRequest.authRequired !== false &&
			error.response?.status === 401 &&
			!originalRequest.url?.includes(REFRESH_ENDPOINT)
		) {
			// A 401 on the already-retried request means the fresh token did not
			// help: the problem is context (e.g. the token's account no longer
			// resolves), not expiry. Refreshing again only rotates tokens, and every
			// rotation hard-reloads the other tabs via the storage listener — an
			// endless reload storm. Terminal instead.
			if (originalRequest._retry) {
				log('401 persisted after token refresh: refresh cannot fix this, clearing auth');
				clearAuthAndRedirect();
				return Promise.reject(error);
			}
			originalRequest._retry = true;

			try {
				const newToken = await refreshAccessToken();
				(originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
				log('Retrying original request after token refresh');
				return api(originalRequest);
			} catch {
				log('Token refresh failed on 401, clearing auth');
				clearAuthAndRedirect();
			}
		}

		// Capture API errors to Sentry (skip 401 as it's expected)
		if (error.response?.status !== 401) {
			captureException(error, {
				tags: {
					[TAGS.ERROR_TYPE]: error.response?.status === 403 ? 'rbac_403' : 'axios_response',
					[TAGS.HTTP_STATUS]: error.response?.status?.toString() || 'network_error',
					[TAGS.HTTP_METHOD]: originalRequest?.method?.toUpperCase() || 'unknown'
				},
				contexts: {
					http: {
						method: originalRequest?.method?.toUpperCase(),
						url: originalRequest?.url,
						status_code: error.response?.status,
						request_headers: originalRequest?.headers,
						response_headers: error.response?.headers,
						response_data: error.response?.data
					},
					response: {
						status: error.response?.status,
						statusText: error.response?.statusText,
						data: error.response?.data
					}
				},
				extra: {
					duration,
					baseURL: originalRequest?.baseURL,
					...(error.response?.status === 403 && {
						rbacHint: 'Possible UI/API permission drift'
					})
				}
			});
		}

		// Surface 403 to user (permission drift between FE gating and BE rules)
		if (error.response?.status === 403) {
			const msg =
				(error.response.data as ErrorResponseData | undefined)?.message || 'You do not have permission to perform this action.';
			toast.error(msg);
		}

		log('Response error:', error.response?.status, error.message);
		return Promise.reject(error);
	}
);

// Create a separate axios instance for machine API
const machineApi: AxiosInstance = axios.create({
	baseURL: MACHINE_BASE_URL,
	timeout: API_TIMEOUT,
	headers: {
		'Content-Type': 'application/json'
	}
});

// Apply same interceptors to machine API
machineApi.interceptors.request.use(
	async (config: CustomAxiosInternalRequestConfig): Promise<CustomAxiosInternalRequestConfig> => {
		const startTime = Date.now();
		log('Machine API Request:', config.method?.toUpperCase(), config.url);
		config.__sentryStartTime = startTime;

		if (config.authRequired === false) return config;

		let accessToken = getAccessToken();

		if (accessToken && isTokenExpired(accessToken)) {
			try {
				accessToken = await refreshAccessToken();
			} catch {
				log('Proactive token refresh failed (machine), clearing auth');
				clearAuthAndRedirect();
				return Promise.reject(new Error('Token expired'));
			}
		}

		if (accessToken) {
			(config.headers as Record<string, string>)['Authorization'] = `Bearer ${accessToken}`;
			log('Authorization header set');
		}

		return config;
	},
	(error: AxiosError) => {
		log('Machine API Request error:', error);

		captureException(error, {
			tags: {
				[TAGS.ERROR_TYPE]: 'axios_request',
				[TAGS.API_TYPE]: 'machine'
			},
			contexts: {
				http: {
					method: error.config?.method?.toUpperCase(),
					url: error.config?.url
				}
			}
		});

		return Promise.reject(error);
	}
);

machineApi.interceptors.response.use(
	(response: AxiosResponse) => {
		const configWithSentry = response.config as CustomAxiosInternalRequestConfig;
		const rawDuration = configWithSentry.__sentryStartTime ? Date.now() - configWithSentry.__sentryStartTime : undefined;
		// Validate duration is non-negative (handles clock adjustments)
		const duration = rawDuration !== undefined ? Math.max(0, rawDuration) : undefined;

		log('Machine API Response:', response.status, response.config.url);

		// Only add breadcrumb for slow responses (>1000ms) or errors (status >= 400)
		const method = response.config.method?.toUpperCase() || 'UNKNOWN';
		const url = response.config.url || '';
		const isSlow = duration !== undefined && duration > 1000;
		const isError = response.status >= 400;

		if (isSlow || isError) {
			addBreadcrumb({
				category: 'http',
				type: 'http',
				level: isError ? ERROR_LEVELS.ERROR : ERROR_LEVELS.WARNING,
				message: `[Machine API] ${method} ${url} - ${response.status}${isSlow ? ' (slow)' : ''}`,
				data: {
					method,
					url,
					statusCode: response.status,
					duration: duration !== undefined ? duration : undefined,
					unit: duration !== undefined ? 'millisecond' : undefined,
					isSlow,
					apiType: 'machine'
				}
			});
		}

		return response;
	},
	async (error: AxiosError<ErrorResponseData>) => {
		const errorData = error.response?.data;
		if (errorData) {
			if (typeof errorData.message === 'string') {
				error.message = errorData.message;
			} else if (typeof errorData.details === 'string') {
				error.message = errorData.details;
			} else if (typeof errorData.detail === 'string') {
				error.message = errorData.detail;
			} else if (typeof errorData.reason === 'string') {
				error.message = errorData.reason;
			}
		}

		const originalRequest = error.config as CustomAxiosInternalRequestConfig | undefined;
		const rawDuration = originalRequest?.__sentryStartTime ? Date.now() - originalRequest.__sentryStartTime : undefined;
		// Validate duration is non-negative (handles clock adjustments)
		const duration = rawDuration !== undefined ? Math.max(0, rawDuration) : undefined;

		// Add Sentry breadcrumb for Machine API error (always add for errors)
		addBreadcrumb({
			category: 'http',
			type: 'http',
			level: ERROR_LEVELS.ERROR,
			message: `[Machine API] ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url} - ${error.response?.status || 'Network Error'}`,
			data: {
				method: originalRequest?.method?.toUpperCase(),
				url: originalRequest?.url,
				statusCode: error.response?.status,
				error: error.message,
				duration,
				apiType: 'machine'
			}
		});

		if (originalRequest && originalRequest.authRequired !== false && error.response?.status === 401 && !originalRequest._retry) {
			originalRequest._retry = true;

			try {
				const newToken = await refreshAccessToken();
				(originalRequest.headers as Record<string, string>)['Authorization'] = `Bearer ${newToken}`;
				log('Retrying machine request after token refresh');
				return machineApi(originalRequest);
			} catch {
				log('Token refresh failed on machine 401, clearing auth');
				clearAuthAndRedirect();
			}
		}

		// Surface 403 to user (Machine API)
		if (error.response?.status === 403) {
			const msg =
				(error.response.data as ErrorResponseData | undefined)?.message || 'You do not have permission to perform this action.';
			toast.error(msg);
		}

		// Capture Machine API errors to Sentry (skip 401 as it's expected)
		if (error.response?.status !== 401) {
			captureException(error, {
				tags: {
					[TAGS.ERROR_TYPE]: error.response?.status === 403 ? 'rbac_403' : 'axios_response',
					[TAGS.HTTP_STATUS]: error.response?.status?.toString() || 'network_error',
					[TAGS.HTTP_METHOD]: originalRequest?.method?.toUpperCase() || 'unknown',
					[TAGS.API_TYPE]: 'machine'
				},
				contexts: {
					http: {
						method: originalRequest?.method?.toUpperCase(),
						url: originalRequest?.url,
						status_code: error.response?.status,
						request_headers: originalRequest?.headers,
						response_headers: error.response?.headers,
						response_data: error.response?.data
					},
					response: {
						status: error.response?.status,
						statusText: error.response?.statusText,
						data: error.response?.data
					}
				},
				extra: {
					duration,
					baseURL: originalRequest?.baseURL,
					apiType: 'machine'
				}
			});
		}

		log('Machine API Response error:', error.response?.status, error.message);
		return Promise.reject(error);
	}
);

export { refreshAccessToken as refreshTokens };

/**
 * Bearer header for requests that cannot go through the axios instance
 * (SSE fetch streams). Refreshes proactively exactly like the interceptor.
 */
export const getAuthHeaders = async (): Promise<Record<string, string>> => {
	let accessToken = getAccessToken();
	if (accessToken && isTokenExpired(accessToken)) {
		accessToken = await refreshAccessToken();
	}
	return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
};

// Export the API methods for use throughout your application
export default {
	get: <T = unknown>(url: string, config?: CustomAxiosRequestConfig) => api.get<T>(url, config),
	post: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) => api.post<T>(url, data, config),
	patch: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) => api.patch<T>(url, data, config),
	put: <T = unknown>(url: string, data?: unknown, config?: CustomAxiosRequestConfig) => api.put<T>(url, data, config),
	delete: <T = unknown>(url: string, config?: CustomAxiosRequestConfig) => api.delete<T>(url, config)
	// Extend with other HTTP methods as necessary
};

export type { AxiosResponse as ApiResponse };
